import axios, { AxiosInstance, AxiosError } from 'axios';

// Get API URL from environment or default to localhost
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('adminToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('adminToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(email: string, password: string) {
    const response = await this.api.post('/auth/login-unified', { email, password });
    return response.data;
  }

  // Dashboard Stats
  async getDashboardStats() {
    const response = await this.api.get('/admin/stats');
    return response.data;
  }

  // Drivers
  async getDrivers(params?: { isOnline?: boolean; location?: string; page?: number; limit?: number }) {
    const response = await this.api.get('/admin/drivers', { params });
    return response.data;
  }

  async updateDriverLoyalty(driverId: string, points: number) {
    const response = await this.api.patch(`/admin/drivers/${driverId}/loyalty`, { points });
    return response.data;
  }

  async toggleDriverStatus(driverId: string, isActive: boolean) {
    const response = await this.api.patch(`/admin/drivers/${driverId}/status`, { isActive });
    return response.data;
  }

  // Jobs
  async getJobs(params?: { status?: string; page?: number; limit?: number }) {
    const response = await this.api.get('/admin/jobs', { params });
    return response.data;
  }

  async getJobById(jobId: string) {
    const response = await this.api.get(`/admin/jobs/${jobId}`);
    return response.data;
  }

  async getJobOffers(jobId: string) {
    const response = await this.api.get(`/admin/jobs/${jobId}/offers`);
    return response.data;
  }

  async getAvailableDriversForJob(jobId: string) {
    const response = await this.api.get(`/admin/jobs/${jobId}/available-drivers`);
    return response.data;
  }

  async assignJobToDriver(jobId: string, driverId: string) {
    const response = await this.api.post(`/admin/jobs/${jobId}/assign-driver`, { driverId });
    return response.data;
  }

  async cancelJob(jobId: string, reason?: string) {
    const response = await this.api.post(`/admin/jobs/${jobId}/cancel`, { reason });
    return response.data;
  }

  async unassignDriver(jobId: string) {
    const response = await this.api.post(`/admin/jobs/${jobId}/unassign-driver`);
    return response.data;
  }

  async createJob(data: {
    customerId: string;
    serviceType: string;
    pickupLocation: {
      address: string;
      coordinates: [number, number];
    };
    dropoffLocation?: {
      address: string;
      coordinates: [number, number];
    };
    vehicleDetails: {
      type: string;
      plateNumber?: string;
      make?: string;
      model?: string;
      color?: string;
    };
    notes?: string;
  }) {
    const response = await this.api.post('/admin/jobs/create', data);
    return response.data;
  }

  async getCustomers(params?: { search?: string; page?: number; limit?: number }) {
    const response = await this.api.get('/admin/customers', { params });
    return response.data;
  }

  async createCustomer(data: {
    firstName: string;
    lastName: string;
    phone: string;
    email?: string;
    password?: string;
  }) {
    const response = await this.api.post('/admin/customers', data);
    return response.data;
  }

  // Admins
  async getAdmins() {
    const response = await this.api.get('/admin/admins');
    return response.data;
  }

  async createAdmin(data: { email: string; password: string; firstName: string; lastName: string }) {
    const response = await this.api.post('/admin/admins', data);
    return response.data;
  }

  async updateAdminStatus(adminId: string, isActive: boolean) {
    const response = await this.api.patch(`/admin/admins/${adminId}/status`, { isActive });
    return response.data;
  }

  // Pricing
  async getAllPricing() {
    const response = await this.api.get('/pricing');
    return response.data;
  }

  async getPricingByServiceType(serviceType: string) {
    const response = await this.api.get(`/pricing/${serviceType}`);
    return response.data;
  }

  async updatePricing(serviceType: string, data: any) {
    const response = await this.api.put(`/pricing/${serviceType}`, data);
    return response.data;
  }

  async deactivatePricing(serviceType: string) {
    const response = await this.api.delete(`/pricing/${serviceType}`);
    return response.data;
  }

  // Withdrawals
  async getAllWithdrawals(params?: { status?: string; driverId?: string; page?: number; limit?: number }) {
    const response = await this.api.get('/admin/withdrawals', { params });
    return response.data;
  }

  async approveWithdrawal(withdrawalId: string) {
    const response = await this.api.put(`/admin/withdrawals/${withdrawalId}/approve`);
    return response.data;
  }

  async rejectWithdrawal(withdrawalId: string, notes: string) {
    const response = await this.api.put(`/admin/withdrawals/${withdrawalId}/reject`, { notes });
    return response.data;
  }

  async completeWithdrawal(withdrawalId: string, paymentReference: string, paymentMethod: string, notes?: string) {
    const response = await this.api.put(`/admin/withdrawals/${withdrawalId}/complete`, {
      paymentReference,
      paymentMethod,
      notes,
    });
    return response.data;
  }

  // Offers
  async getOffers() {
    const response = await this.api.get('/admin/offers');
    return response.data;
  }

  async createOffer(data: {
    code: string;
    title: string;
    description: string;
    discountType: 'percentage' | 'fixed';
    discountValue: number;
    minOrderValue?: number;
    maxDiscountAmount?: number;
    conditions: {
      type: 'first_time_user' | 'min_orders_completed' | 'all_users';
      value?: number;
    };
    validFrom: string;
    validUntil: string;
    usageLimit?: number;
  }) {
    const response = await this.api.post('/admin/offers', data);
    return response.data;
  }

  async updateOffer(offerId: string, data: any) {
    const response = await this.api.put(`/admin/offers/${offerId}`, data);
    return response.data;
  }

  async toggleOfferStatus(offerId: string, isActive: boolean) {
    const response = await this.api.patch(`/admin/offers/${offerId}/status`, { isActive });
    return response.data;
  }

  async deleteOffer(offerId: string) {
    const response = await this.api.delete(`/admin/offers/${offerId}`);
    return response.data;
  }

  // Vendors
  async getVendors(params?: { search?: string; page?: number; limit?: number }) {
    const response = await this.api.get('/admin/vendors', { params });
    return response.data;
  }

  async getVendorById(vendorId: string) {
    const response = await this.api.get(`/admin/vendors/${vendorId}`);
    return response.data;
  }

  async createVendor(data: {
    firstName: string;
    lastName: string;
    email?: string;
    phone: string;
    password?: string;
    businessName: string;
    city: string;
    businessRegistration?: string;
    gstNumber?: string;
    address?: string;
  }) {
    const response = await this.api.post('/admin/vendors', data);
    return response.data;
  }

  async updateVendor(vendorId: string, data: any) {
    const response = await this.api.patch(`/admin/vendors/${vendorId}`, data);
    return response.data;
  }

  async toggleVendorStatus(vendorId: string, isActive: boolean) {
    const response = await this.api.patch(`/admin/vendors/${vendorId}/status`, { isActive });
    return response.data;
  }

  async deleteVendor(vendorId: string) {
    const response = await this.api.delete(`/admin/vendors/${vendorId}`);
    return response.data;
  }

  // Vendor Dashboard APIs (for logged-in vendors)
  async getVendorDashboardStats() {
    const response = await this.api.get('/vendor/dashboard/stats');
    return response.data;
  }

  async getVendorFleetDrivers() {
    const response = await this.api.get('/vendor/drivers');
    return response.data;
  }

  async removeDriverFromFleet(driverId: string) {
    const response = await this.api.post(`/vendor/drivers/${driverId}/remove`);
    return response.data;
  }

  async getVendorPendingJobs() {
    const response = await this.api.get('/vendor/jobs/pending');
    return response.data;
  }

  async getVendorActiveJobs() {
    const response = await this.api.get('/vendor/jobs/active');
    return response.data;
  }

  async getVendorInProgressJobs() {
    const response = await this.api.get('/vendor/jobs/in-progress');
    return response.data;
  }

  async getVendorCompletedJobs(params?: { limit?: number; offset?: number }) {
    const response = await this.api.get('/vendor/jobs/completed', { params });
    return response.data;
  }

  async assignJobToVendorDriver(jobId: string, driverId: string) {
    const response = await this.api.post(`/vendor/jobs/${jobId}/assign`, { driverId });
    return response.data;
  }

  async getVendorEarnings(params?: { startDate?: string; endDate?: string; limit?: number; offset?: number }) {
    const response = await this.api.get('/vendor/earnings', { params });
    return response.data;
  }
}

export default new ApiService();
